<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import offersAPI from '../../../../api/offers';
import KanbanAPI from '../../../../api/kanban';
import {
  formatBRL,
  handleCurrencyKeydown,
} from '../helpers/useCurrencyFormat';

// Props
const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  isStacklab: {
    type: Boolean,
    default: false,
  },
});

// Emits
const emit = defineEmits(['item-updated']);

const { t } = useI18n();

// Estado
const loading = ref(false);
const saving = ref(false);
const catalogOffers = ref([]);
const linkedOffers = ref([]);
const showCatalogModal = ref(false);
const searchTerm = ref('');
const hasChanges = ref(false);

// Estado para produto manual
const showManualOfferForm = ref(false);
const manualOffer = ref({
  description: '',
  value: 0,
  currency: { symbol: 'R$', code: 'BRL', locale: 'pt-BR' },
});

// Estado para formatação de moeda do form manual
const manualValueCents = ref(0);
const manualValueDisplay = ref('0,00');

// Computed - filtro de busca
const filteredCatalogOffers = computed(() => {
  if (!searchTerm.value) return catalogOffers.value;
  const term = searchTerm.value.toLowerCase();
  return catalogOffers.value.filter(
    offer =>
      offer.name?.toLowerCase().includes(term) ||
      offer.type?.toLowerCase().includes(term)
  );
});

// Função para construir URL da imagem do produto
const getOfferImageUrl = offer => {
  if (!offer?.id) {
    console.debug('[getOfferImageUrl] offer sem id:', offer);
    return null;
  }
  // Usa o account_id do próprio item Kanban
  const accountId = props.item?.account_id;
  if (!accountId) {
    console.debug('[getOfferImageUrl] sem accountId');
    return null;
  }
  const url = `/api/v1/accounts/${accountId}/offers/${offer.id}/image`;
  console.debug('[getOfferImageUrl] URL gerada:', url);
  return url;
};

// Função para formatar valor
const formatCurrency = (value) => {
  const numValue = parseFloat(value) || 0;
  return `R$ ${formatBRL(numValue)}`;
};

// Handler for cents-first currency input (manual form)
const onManualValueKeydown = (event) => {
  const result = handleCurrencyKeydown(event, manualValueCents.value);
  if (result) {
    manualValueCents.value = result.cents;
    manualValueDisplay.value = result.display;
    manualOffer.value.value = result.value;
  }
};

// Carregar produtos do catálogo
const fetchCatalogOffers = async () => {
  try {
    loading.value = true;
    const response = await offersAPI.getOffers();
    catalogOffers.value = (response.data || []).map(offer => ({
      id: offer.id,
      name: offer.title,
      value: offer.value,
      discount_percentage: offer.value,
      currency: offer.currency,
      type: offer.type,
      image_url: offer.image_url,
    }));
  } catch (error) {
    console.error('Erro ao carregar produtos do catálogo:', error);
  } finally {
    loading.value = false;
  }
};

// Carregar Produtos Vinculados do item
const loadLinkedOffers = () => {
  const offers = props.item?.item_details?.offers || [];
  linkedOffers.value = offers.map(offer => ({
    ...offer,
    // Garantir que id está mapeado corretamente (pode vir como offer_id do backend)
    id: offer.id || offer.offer_id,
    // Mapear title para description (backend salva como title, frontend exibe como description)
    description: offer.description || offer.title || offer.name || `Produto #${offer.id}`,
    original_value: offer.original_value ?? offer.value,
  }));
  hasChanges.value = false;
};

// Verificar se produto já está vinculado
const isOfferLinked = offerId => {
  return linkedOffers.value.some(o => o.id === offerId);
};

// Adicionar produto do catálogo
const addOfferFromCatalog = offer => {
  if (isOfferLinked(offer.id)) {
    emitter.emit('newToastMessage', {
      message: 'Esta produto já está vinculado',
      action: { type: 'warning' },
    });
    return;
  }

  linkedOffers.value.push({
    id: offer.id,
    description: offer.name,
    value: offer.discount_percentage || offer.value,
    original_value: offer.discount_percentage || offer.value,
    currency: {
      symbol: offer.currency === 'BRL' ? 'R$' : offer.currency === 'USD' ? '$' : '€',
      code: offer.currency || 'BRL',
      locale: offer.currency === 'BRL' ? 'pt-BR' : offer.currency === 'USD' ? 'en-US' : 'de-DE',
    },
  });

  hasChanges.value = true;
  showCatalogModal.value = false;
};

// Adicionar produto manual
const addManualOffer = () => {
  const description = manualOffer.value.description?.trim();
  const value = parseFloat(manualOffer.value.value);
  
  // Validação: descrição obrigatória e valor deve ser número válido >= 0
  if (!description) {
    emitter.emit('newToastMessage', {
      message: 'Preencha a descrição do produto',
      action: { type: 'error' },
    });
    return;
  }
  
  if (Number.isNaN(value) || value < 0) {
    emitter.emit('newToastMessage', {
      message: 'Valor deve ser um número válido maior ou igual a zero',
      action: { type: 'error' },
    });
    return;
  }

  linkedOffers.value.push({
    manual: true,
    description: description,
    value: value,
    original_value: value,
    currency: { ...manualOffer.value.currency },
  });

  // Limpar form
  manualOffer.value = {
    description: '',
    value: 0,
    currency: { symbol: 'R$', code: 'BRL', locale: 'pt-BR' },
  };
  manualValueCents.value = 0;
  manualValueDisplay.value = '0,00';
  showManualOfferForm.value = false;
  hasChanges.value = true;
};



// Remover produto
const removeOffer = index => {
  // Bounds checking defensivo
  if (index < 0 || index >= linkedOffers.value.length) {
    console.warn(`removeOffer: índice inválido ${index}, length: ${linkedOffers.value.length}`);
    return;
  }
  linkedOffers.value.splice(index, 1);
  hasChanges.value = true;
};

// Tooltip com valor original
const getOriginalValueTooltip = offer => {
  if (!offer) return 'Editar valor do produto';

  const originalValue = offer.original_value;
  const currentValue = offer.value;

  if (
    typeof originalValue === 'number' &&
    !Number.isNaN(originalValue) &&
    originalValue !== currentValue
  ) {
    return `Valor original: R$ ${formatBRL(originalValue)}`;
  }

  return 'Editar valor do produto';
};

// Salvar alterações
const saveChanges = async () => {
  // Validação crítica: props.item deve existir
  if (!props.item?.id) {
    console.error('saveChanges: props.item ou props.item.id não definido');
    emitter.emit('newToastMessage', {
      message: 'Erro interno: item não identificado',
      action: { type: 'error' },
    });
    return;
  }

  try {
    saving.value = true;

    // Preparar payload com fallback defensivo para item_details
    const currentItemDetails = props.item.item_details || {};
    
    // Garantir que cada produto tenha o id explicitamente definido
    const offersToSave = linkedOffers.value.map(offer => ({
      id: offer.id, // Preservar id explicitamente
      offer_id: offer.id, // Também salvar como offer_id para compatibilidade
      description: offer.description,
      value: offer.value,
      original_value: offer.original_value,
      currency: offer.currency,
      manual: offer.manual || false,
    }));
    
    const payload = {
      item_details: {
        ...currentItemDetails,
        offers: offersToSave,
        // Atualizar valor principal: primeiro produto ou null se não houver produtos
        value: linkedOffers.value.length > 0 ? linkedOffers.value[0].value : null,
      },
    };

    await KanbanAPI.updateItem(props.item.id, payload);

    emitter.emit('newToastMessage', {
      message: 'Produtos salvos com sucesso',
      action: { type: 'success' },
    });

    hasChanges.value = false;
    emit('item-updated');
  } catch (error) {
    console.error('Erro ao salvar produtos:', error);
    // Feedback de erro mais informativo
    const errorMessage = error?.response?.data?.message || error?.message || 'Erro desconhecido';
    emitter.emit('newToastMessage', {
      message: `Erro ao salvar produtos: ${errorMessage}`,
      action: { type: 'error' },
    });
  } finally {
    saving.value = false;
  }
};

// Cancelar alterações
const cancelChanges = () => {
  loadLinkedOffers();
  hasChanges.value = false;
};

// Lifecycle
onMounted(() => {
  loadLinkedOffers();
  fetchCatalogOffers();
});

// Watch para recarregar quando item mudar
watch(
  () => props.item?.id,
  () => {
    loadLinkedOffers();
  }
);
</script>

<template>
  <div class="offers-tab space-y-4">
    <!-- Header com ações -->
    <div class="flex items-center justify-between">
      <h3 class="text-sm font-semibold text-slate-700 dark:text-slate-300">
        Produtos Vinculados
        <span
          v-if="linkedOffers.length > 0"
          class="ml-2 text-xs font-normal text-slate-500"
        >
          ({{ linkedOffers.length }})
        </span>
      </h3>
      
      <!-- Botões de ação -->
      <div class="flex items-center gap-2">
        <button
          class="px-3 py-1.5 text-xs font-medium text-woot-600 dark:text-woot-400 hover:bg-woot-50 dark:hover:bg-woot-900/20 rounded-lg transition-colors"
          @click="showCatalogModal = true"
        >
          <fluent-icon icon="add" size="14" class="mr-1" />
          Catálogo
        </button>
        <button
          class="px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          @click="showManualOfferForm = !showManualOfferForm"
        >
          <fluent-icon icon="edit" size="14" class="mr-1" />
          Manual
        </button>
      </div>
    </div>

    <!-- Form de produto manual -->
    <div
      v-if="showManualOfferForm"
      class="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700"
    >
      <div class="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
            Descrição
          </label>
          <input
            v-model="manualOffer.description"
            type="text"
            class="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-1 focus:ring-woot-500 focus:border-woot-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            placeholder="Nome do produto"
          />
        </div>
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
            Valor
          </label>
          <div class="flex items-center gap-2">
            <span class="text-sm text-slate-500">R$</span>
            <input
              type="text"
              inputmode="numeric"
              :value="manualValueDisplay"
              class="flex-1 px-3 py-2 text-sm text-right border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-1 focus:ring-woot-500 focus:border-woot-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              placeholder="0,00"
              @keydown="onManualValueKeydown"
            />
          </div>
        </div>
      </div>
      <div class="flex justify-end gap-2">
        <button
          class="px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg"
          @click="showManualOfferForm = false"
        >
          Cancelar
        </button>
        <button
          class="px-3 py-1.5 text-xs font-medium text-white bg-woot-600 hover:bg-woot-700 rounded-lg"
          @click="addManualOffer"
        >
          Adicionar
        </button>
      </div>
    </div>

    <!-- Lista de Produtos Vinculados -->
    <div v-if="linkedOffers.length > 0" class="space-y-2">
      <div
        v-for="(offer, index) in linkedOffers"
        :key="index"
        class="flex items-center gap-3 p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 transition-colors"
      >
        <!-- Thumbnail -->
        <div class="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-700">
          <img
            v-if="offer.id && !offer.manual"
            :src="getOfferImageUrl(offer)"
            :alt="offer.description"
            class="w-full h-full object-cover"
            @error="$event.target.style.display = 'none'"
          />
          <div
            v-else
            class="w-full h-full flex items-center justify-center text-slate-400"
          >
            <fluent-icon :icon="offer.manual ? 'edit' : 'gift'" size="20" />
          </div>
        </div>

        <!-- Info -->
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2">
            <span class="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">
              {{ offer.description }}
            </span>
            <span
              v-if="offer.manual"
              class="px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded"
            >
              Manual
            </span>
          </div>
          <div
            v-if="offer.original_value && offer.original_value !== offer.value"
            class="text-xs text-slate-500 dark:text-slate-400"
          >
            Original: {{ formatCurrency(offer.original_value) }}
          </div>
        </div>

        <!-- Valor editável -->
        <div class="flex items-center gap-1">
          <span class="text-xs text-slate-500 dark:text-slate-400">
            R$
          </span>
          <span
            class="w-24 px-2 py-1.5 text-sm text-right font-medium text-green-600 dark:text-green-400 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg"
            :title="getOriginalValueTooltip(offer)"
          >
            {{ formatBRL(offer.value) }}
          </span>
        </div>

        <!-- Botão remover -->
        <button
          type="button"
          @click="removeOffer(index)"
          class="p-1.5 text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
          title="Remover produto"
        >
          <fluent-icon icon="delete" size="16" />
        </button>
      </div>
    </div>

    <!-- Estado vazio -->
    <div
      v-else
      class="flex flex-col items-center justify-center py-8 text-center"
    >
      <div class="w-12 h-12 mb-3 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
        <fluent-icon icon="gift" size="24" class="text-slate-400" />
      </div>
      <p class="text-sm text-slate-600 dark:text-slate-400 mb-1">
        Nenhum produto vinculado
      </p>
      <p class="text-xs text-slate-500 dark:text-slate-500">
        Clique em "Catálogo" ou "Manual" para adicionar produtos
      </p>
    </div>

    <!-- Botões de salvar/cancelar -->
    <div
      v-if="hasChanges"
      class="flex items-center justify-end gap-2 pt-4 border-t border-slate-200 dark:border-slate-700"
    >
      <button
        class="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
        :disabled="saving"
        @click="cancelChanges"
      >
        Cancelar
      </button>
      <button
        class="px-4 py-2 text-sm font-medium text-white bg-woot-600 hover:bg-woot-700 rounded-lg transition-colors disabled:opacity-50"
        :disabled="saving"
        @click="saveChanges"
      >
        <span v-if="saving" class="inline-flex items-center">
          <span class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
          Salvando...
        </span>
        <span v-else>Salvar Produtos</span>
      </button>
    </div>

    <!-- Modal do catálogo -->
    <teleport to="body">
      <div
        v-if="showCatalogModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        @click.self="showCatalogModal = false"
      >
        <div class="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
          <!-- Header -->
          <div class="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
            <h3 class="text-lg font-semibold text-slate-800 dark:text-slate-200">
              Selecionar do Catálogo
            </h3>
            <button
              class="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-lg"
              @click="showCatalogModal = false"
            >
              <fluent-icon icon="dismiss" size="20" />
            </button>
          </div>

          <!-- Busca -->
          <div class="p-4 border-b border-slate-200 dark:border-slate-700">
            <input
              v-model="searchTerm"
              type="text"
              class="w-full px-4 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-1 focus:ring-woot-500 focus:border-woot-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              placeholder="Buscar produtos..."
            />
          </div>

          <!-- Lista -->
          <div class="flex-1 overflow-y-auto p-2">
            <div v-if="loading" class="flex items-center justify-center py-8">
              <span class="w-6 h-6 border-2 border-woot-500 border-t-transparent rounded-full animate-spin" />
            </div>
            <div v-else-if="filteredCatalogOffers.length === 0" class="text-center py-8 text-slate-500">
              Nenhum produto encontrado
            </div>
            <div v-else class="space-y-1">
              <button
                v-for="offer in filteredCatalogOffers"
                :key="offer.id"
                class="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-left"
                :class="{ 'opacity-50': isOfferLinked(offer.id) }"
                :disabled="isOfferLinked(offer.id)"
                @click="addOfferFromCatalog(offer)"
              >
                <!-- Thumbnail -->
                <div class="flex-shrink-0 w-10 h-10 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-700">
                  <img
                    :src="getOfferImageUrl(offer)"
                    :alt="offer.name"
                    class="w-full h-full object-cover"
                    @error="$event.target.style.display = 'none'"
                  />
                </div>
                <!-- Info -->
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2">
                    <span class="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">
                      {{ offer.name }}
                    </span>
                    <span
                      v-if="isOfferLinked(offer.id)"
                      class="px-1.5 py-0.5 text-[10px] bg-green-100 text-green-700 rounded"
                    >
                      Vinculada
                    </span>
                  </div>
                  <span class="text-xs text-slate-500">{{ offer.type }}</span>
                </div>
                <!-- Valor -->
                <span class="text-sm font-medium text-green-600 dark:text-green-400">
                  R$ {{ formatBRL(offer.value || offer.discount_percentage) }}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<style scoped>
.offers-tab {
  @apply min-h-[200px];
}
</style>

